/**
 * useTemplates Hook
 * Manages template data operations with localStorage persistence
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  MOCK_TEMPLATES, 
  MOCK_TEMPLATE_SECTIONS,
  COUNTRIES,
} from '@/constants/beStudyMockData';

const STORAGE_KEY = 'be_study_templates';

/**
 * Get initial templates from localStorage or use mock data
 */
const getInitialTemplates = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading templates from localStorage:', error);
  }
  return MOCK_TEMPLATES;
};

/**
 * Save templates to localStorage
 */
const saveToStorage = (templates) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error('Error saving templates to localStorage:', error);
  }
};

export function useTemplates() {
  const [templates, setTemplates] = useState(getInitialTemplates);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist to localStorage whenever templates change
  useEffect(() => {
    saveToStorage(templates);
  }, [templates]);

  // Get all templates
  const getAllTemplates = useCallback(() => {
    return templates;
  }, [templates]);

  // Get template by ID
  const getTemplate = useCallback((id) => {
    return templates.find(t => t.id === id);
  }, [templates]);

  // Get active templates
  const activeTemplates = useMemo(() => {
    return templates.filter(t => t.status === 'ACTIVE');
  }, [templates]);

  // Generate unique ID
  const generateId = useCallback(() => {
    const maxId = templates.reduce((max, t) => {
      const num = parseInt(t.id.replace('TMP-', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `TMP-${String(maxId + 1).padStart(3, '0')}`;
  }, [templates]);

  // Create new template
  const createTemplate = useCallback((templateData) => {
    setIsLoading(true);
    try {
      const newTemplate = {
        id: generateId(),
        ...templateData,
        version: '1.0',
        sections: templateData.sections || [],
        createdBy: 'USR-001', // Current user
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: templateData.status || 'ACTIVE',
      };
      
      setTemplates(prev => [...prev, newTemplate]);
      setIsLoading(false);
      return { success: true, template: newTemplate };
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, [generateId]);

  // Update template
  const updateTemplate = useCallback((id, updates) => {
    setIsLoading(true);
    try {
      setTemplates(prev => 
        prev.map(t => 
          t.id === id 
            ? { 
                ...t, 
                ...updates, 
                updatedAt: new Date().toISOString(),
                version: incrementVersion(t.version),
              }
            : t
        )
      );
      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // Helper to increment version
  const incrementVersion = (version) => {
    const parts = version.split('.');
    const minor = parseInt(parts[1] || '0', 10) + 1;
    return `${parts[0]}.${minor}`;
  };

  // Delete template
  const deleteTemplate = useCallback((id) => {
    setIsLoading(true);
    try {
      setTemplates(prev => prev.filter(t => t.id !== id));
      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // Duplicate template
  const duplicateTemplate = useCallback((id) => {
    const template = templates.find(t => t.id === id);
    if (!template) return { success: false, error: 'Template not found' };

    const newTemplate = {
      ...template,
      id: generateId(),
      name: `${template.name} (Copy)`,
      version: '1.0',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sections: template.sections.map((s, i) => ({
        ...s,
        id: `SEC-${Date.now()}-${i}`,
      })),
    };

    setTemplates(prev => [...prev, newTemplate]);
    return { success: true, template: newTemplate };
  }, [templates, generateId]);

  // Add section to template
  const addSection = useCallback((templateId, sectionData) => {
    setTemplates(prev => 
      prev.map(t => {
        if (t.id !== templateId) return t;
        
        const newSection = {
          id: `SEC-${Date.now()}`,
          order: t.sections.length + 1,
          required: true,
          ...sectionData,
        };
        
        return {
          ...t,
          sections: [...t.sections, newSection],
          updatedAt: new Date().toISOString(),
        };
      })
    );
    return { success: true };
  }, []);

  // Update section
  const updateSection = useCallback((templateId, sectionId, updates) => {
    setTemplates(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        
        return {
          ...t,
          sections: t.sections.map(s => 
            s.id === sectionId ? { ...s, ...updates } : s
          ),
          updatedAt: new Date().toISOString(),
        };
      })
    );
    return { success: true };
  }, []);

  // Delete section
  const deleteSection = useCallback((templateId, sectionId) => {
    setTemplates(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        
        const updatedSections = t.sections
          .filter(s => s.id !== sectionId)
          .map((s, index) => ({ ...s, order: index + 1 }));
        
        return {
          ...t,
          sections: updatedSections,
          updatedAt: new Date().toISOString(),
        };
      })
    );
    return { success: true };
  }, []);

  // Reorder sections
  const reorderSections = useCallback((templateId, newOrder) => {
    setTemplates(prev =>
      prev.map(t => {
        if (t.id !== templateId) return t;
        
        return {
          ...t,
          sections: newOrder.map((sectionId, index) => {
            const section = t.sections.find(s => s.id === sectionId);
            return { ...section, order: index + 1 };
          }),
          updatedAt: new Date().toISOString(),
        };
      })
    );
    return { success: true };
  }, []);

  // Simulate AI extraction from uploaded document
  const extractSectionsFromDocument = useCallback(async (file) => {
    setIsLoading(true);
    
    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Return mock extracted sections
    const extractedSections = [
      { id: `SEC-${Date.now()}-1`, title: 'Executive Summary', order: 1, required: true, description: 'Overview of the study' },
      { id: `SEC-${Date.now()}-2`, title: 'Introduction', order: 2, required: true, description: 'Background information' },
      { id: `SEC-${Date.now()}-3`, title: 'Study Objectives', order: 3, required: true, description: 'Primary and secondary objectives' },
      { id: `SEC-${Date.now()}-4`, title: 'Methodology', order: 4, required: true, description: 'Study design and methods' },
      { id: `SEC-${Date.now()}-5`, title: 'Results', order: 5, required: true, description: 'Study findings' },
      { id: `SEC-${Date.now()}-6`, title: 'Discussion', order: 6, required: true, description: 'Interpretation of results' },
      { id: `SEC-${Date.now()}-7`, title: 'Conclusions', order: 7, required: true, description: 'Summary of conclusions' },
      { id: `SEC-${Date.now()}-8`, title: 'References', order: 8, required: false, description: 'Bibliography' },
    ];
    
    setIsLoading(false);
    return { success: true, sections: extractedSections };
  }, []);

  // Search templates
  const searchTemplates = useCallback((query) => {
    const lowerQuery = query.toLowerCase();
    return templates.filter(t => 
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description?.toLowerCase().includes(lowerQuery) ||
      t.country.toLowerCase().includes(lowerQuery)
    );
  }, [templates]);

  // Filter templates by country
  const filterByCountry = useCallback((countryCode) => {
    if (!countryCode) return templates;
    return templates.filter(t => t.country === countryCode);
  }, [templates]);

  // Reset to initial mock data
  const resetTemplates = useCallback(() => {
    setTemplates(MOCK_TEMPLATES);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    templates,
    activeTemplates,
    isLoading,
    error,
    getAllTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    addSection,
    updateSection,
    deleteSection,
    reorderSections,
    extractSectionsFromDocument,
    searchTemplates,
    filterByCountry,
    resetTemplates,
    countries: COUNTRIES,
  };
}

export default useTemplates;
